import React, { useState, useEffect } from "react";
import "../css/reports.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  BarChart3,
  FileText,
  Sheet,
  Search,
  ClipboardList,
} from "lucide-react";

// ✅ Normalized API setup (works for local, Render, Vite, CRA)
const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");
const apiPath = (path) => `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;

// ✅ Optional: add Auth header (if backend uses tokens)
const getAuth = () => {
  const raw = localStorage.getItem("rcs_auth") || sessionStorage.getItem("rcs_auth");
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const authHeader = () => {
  const s = getAuth();
  return s?.access_token ? { Authorization: `Bearer ${s.access_token}` } : {};
};

const Reports = () => {

  const [reportType, setReportType] = useState("customer");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [subSelection, setSubSelection] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reportData, setReportData] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [productSuggestions, setProductSuggestions] = useState([]);

  const extractQuantityAndPrice = (data) => {
    // ✅ New format: array of items
    if (Array.isArray(data)) {
      const item = data[0]; // first item only
      return {
        quantity: parseFloat(item?.quantity) || 0,
        price: parseFloat(item?.price) || 0,
      };
    }
  
    // ✅ New format: single object
    if (typeof data === "object" && data !== null) {
      return {
        quantity: parseFloat(data.quantity) || 0,
        price: parseFloat(data.price) || 0,
      };
    }
  
    // ✅ Old string format: "Product x:2:1000"
    if (typeof data === "string") {
      const match = data.match(/x\s*:?(-?\d+)\s*:?(-?\d+(\.\d+)?)/);
      if (match) {
        return {
          quantity: parseFloat(match[1]) || 0,
          price: parseFloat(match[2]) || 0,
        };
      }
    }
  
    return { quantity: 0, price: 0 };
  };
  
  const formatItems = (items) => {
    try {
      let parsed = [];
  
      if (Array.isArray(items)) {
        parsed = items;
      } else if (typeof items === "string") {
        parsed = JSON.parse(items);
      }
  
      return parsed.map(item =>
        `${item.product || "Unnamed"} x${item.quantity} @ ${parseFloat(item.price).toLocaleString("en-US", {
          minimumFractionDigits: 2
        })}`
      ).join(", ");
    } catch (err) {
      return typeof items === "string" ? items : "–";
    }
  };
  
  
  const exportToPDF = () => {
    if (reportData.length === 0) return alert("No data to export");
  
    const doc = new jsPDF({ orientation: "landscape" });
  
    // 🔰 Set up fonts
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("RCS TOPUP AND BILLING ENTITY", 14, 14);
    doc.setFont(undefined, "normal");
    doc.text("22 BELLE PLAINE WAKENAAM", 14, 20);
    doc.text("(592) 662-7987  //  (592) 649-1514", 14, 26);
  
    // 🔰 Draw separator line
    doc.setLineWidth(0.5);
    doc.line(14, 30, 280, 30); // from x=14 to end of page (adjust if needed)
  
    // 🧾 Title for the report below header
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("RCS POS - Report", 14, 38); // title after header
  
    const headers = Object.keys(reportData[0] || {}).filter(Boolean);

    const body = reportData.map(row =>
      headers.map(key => {
        const val = row[key];
        
        if (key === "items") return formatItems(val);
    
        return typeof val === "number"
          ? val.toLocaleString("en-US", { minimumFractionDigits: 2 })
          : String(val);
      })
    );
    
  
    const totalsRow = headers.map((key, i) => {
      if (i === 0) return "TOTAL";
    
      if (reportType === "product") {
        if (key === "subtotal") {
          const subtotalSum = reportData.reduce((sum, row) => {
            const q = parseFloat(row.quantity) || 0;
            const p = parseFloat(row.price) || 0;
            return sum + (q * p);
          }, 0);
          return subtotalSum.toLocaleString("en-US", { minimumFractionDigits: 2 });
        }
    
        if (key === "total") {
          const totalSum = reportData.reduce((sum, row) => {
            const q = parseFloat(row.quantity) || 0;
            const p = parseFloat(row.price) || 0;
            const discount = parseFloat(row.discount) || 0;
            const subtotal = q * p;
            return sum + (subtotal - discount);
          }, 0);
          return totalSum.toLocaleString("en-US", { minimumFractionDigits: 2 });
        }
      }
    
      if (typeof reportData[0][key] === "number") {
        const sum = reportData.reduce((acc, row) => acc + (parseFloat(row[key]) || 0), 0);
        return sum.toLocaleString("en-US", { minimumFractionDigits: 2 });
      }
    
      return "";
    });
    
    
  
    // Basic column styles for simple table
    const columnStyles = {};
    headers.forEach((key, i) => {
      if (["subtotal", "discount", "total"].includes(key)) {
        columnStyles[i] = { halign: "right" };
      } else {
        columnStyles[i] = { halign: "left" };
      }
    });
  
    autoTable(doc, {
      head: [
        headers.map(h =>
          (h || "").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
        )
      ],
      body:
        reportType === "all-products" || reportType === "product"
          ? [...body, totalsRow]
          : body,
      startY: 42,
      theme: "grid",
      styles: { fontSize: 10 },
      columnStyles,
      headStyles: {
        fillColor: [22, 160, 133],
        halign: "center"
      },
    
      // ✅ Totals styling logic
      didParseCell: function (data) {
        const isTotalsRow = reportType === "all-products" || reportType === "product";
        const isLastRow = data.row.index === body.length;
    
        if (data.section === "body" && isTotalsRow && isLastRow) {
          if (data.column.index === 0) {
            data.cell.styles.fontStyle = "bold"; // "TOTAL" label
          } else if (!isNaN(Number((data.cell.raw || "").replace(/,/g, "")))) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = [220, 53, 69]; // red for totals
          }
        }
      },
    
      // ✅ Footer info
      didDrawPage: (data) => {
        doc.setFontSize(9);
        doc.text(
          `Generated on: ${new Date().toLocaleString()}`,
          14,
          doc.internal.pageSize.height - 10
        );
      }
    });
    
  
    doc.save("report.pdf");
  };
  
  
  
const exportToCSV = () => {
  if (reportData.length === 0) return alert("No data to export");

  const headers = Object.keys(reportData[0]);
  const rows = reportData.map(row =>
    headers.map(field => {
      if (field === "items") return `"${formatItems(row[field])}"`;
      return JSON.stringify(row[field] ?? "");
    })
  );
  

  // Add totals row
  const numericCols = headers.map((key, i) =>
    typeof reportData[0][key] === "number" ? i : null
  );

  const totals = (reportType === "all-products" || reportType === "product")
  ? headers.map((key, i) => {
      if (i === 0) return `"Total"`;

      if (reportType === "product") {
        if (key === "subtotal") {
          const subtotal = reportData.reduce((sum, row) => {
            const q = parseFloat(row.quantity) || 0;
            const p = parseFloat(row.price) || 0;
            return sum + (q * p);
          }, 0);
          return `"${subtotal.toFixed(2)}"`;
        }

        if (key === "total") {
          const total = reportData.reduce((sum, row) => {
            const q = parseFloat(row.quantity) || 0;
            const p = parseFloat(row.price) || 0;
            const d = parseFloat(row.discount) || 0;
            const s = q * p;
            return sum + (s - d);
          }, 0);
          return `"${total.toFixed(2)}"`;
        }
      }

      if (typeof reportData[0][key] === "number") {
        const sum = reportData.reduce((acc, row) => acc + (parseFloat(row[key]) || 0), 0);
        return `"${sum.toFixed(2)}"`;
      }

      return `""`;
    })
  : [];


  const csvContent = [
    headers.join(","),
    ...rows.map(r => r.join(",")),
    totals.join(","),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", "report.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


  useEffect(() => {
    if (reportType === "customer" && searchQuery.length >= 2) {
      fetch(apiPath(`/reports/customers/search?query=${encodeURIComponent(searchQuery)}`), {
  headers: { ...authHeader() },
})

        .then(res => res.json())
        .then(setCustomerSuggestions)
        .catch(console.error);
    }
  }, [searchQuery, reportType]);

  useEffect(() => {
    if (reportType === "product" && searchQuery.length >= 2) {
      fetch(apiPath(`/reports/products/search?query=${encodeURIComponent(searchQuery)}`), {
  headers: { ...authHeader() },
})

        .then(res => res.json())
        .then(setProductSuggestions)
        .catch(console.error);
    }
  }, [searchQuery, reportType]);

  const aggregateProducts = (data) => {
    const productMap = {};
  
    data.forEach((row) => {
      const productName = row.product || row.items || "Unknown Product";
      const subtotal = parseFloat(row.subtotal) || 0;
      const discount = parseFloat(row.discount) || 0;
      const total = subtotal - discount;
  
      if (!productMap[productName]) {
        productMap[productName] = {
          product: productName,
          subtotal: 0,
          discount: 0,
          total: 0,
        };
      }
  
      productMap[productName].subtotal += subtotal;
      productMap[productName].discount += discount;
      productMap[productName].total += total;
    });
  
    return Object.values(productMap);
  };
  
  const fetchReport = async () => {
    let url = apiPath(`/reports?type=${reportType}`);

  
    // Validate and append parameters for specific reports
    switch (reportType) {
      case "customer":
      case "product":
        if (!subSelection || !startDate || !endDate) {
          alert("Please select a value, start date, and end date.");
          return;
        }
        url += `&filter=${encodeURIComponent(subSelection)}&start=${startDate}&end=${endDate}`;
        break;
  
      case "payment":
        if (!subSelection || !startDate || !endDate) {
          alert("Please select payment method, start date, and end date.");
          return;
        }
        url += `&filter=${encodeURIComponent(subSelection)}&start=${startDate}&end=${endDate}`;
        break;
  
      case "day":
        if (!subSelection) {
          alert("Please select a date.");
          return;
        }
        url += `&filter=${subSelection}`;
        break;
  
      case "range":
        if (!startDate || !endDate) {
          alert("Please select start and end dates.");
          return;
        }
        url += `&start=${startDate}&end=${endDate}`;
        break;
  
      case "all-products":
        if (!startDate || !endDate) {
          alert("Please select start and end dates.");
          return;
        }
        url += `&start=${startDate}&end=${endDate}`;
        break;
  
      default:
        alert("Invalid report type selected.");
        return;
    }
  
    try {
      const res = await fetch(url, { headers: { ...authHeader() } });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      const data = await res.json();
  
      // ✅ Aggregate data for the all-products report
      if (reportType === "all-products") {
        const aggregatedData = aggregateProducts(data);
        setReportData(aggregatedData);
      } else {
        setReportData(data);
      }
    } catch (err) {
      console.error("Fetch error:", err.message);
      alert("Failed to fetch report. Check console for details.");
    }
  };
  
  
  
const renderSearchInput = () => {

  /* =====================================================
      CUSTOMER
  ===================================================== */
  if (reportType === "customer") {

    return (
      <div className="modern-inline-field-wrap">

        <div className="modern-inline-field">

          <label>
            Select Customer
          </label>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) =>
              setSearchQuery(
                e.target.value
              )
            }
            placeholder="Search customer..."
            className="modern-inline-input"
          />

        </div>

        {customerSuggestions.length > 0 && (

          <ul className="modern-selector-dropdown">

            {customerSuggestions.map(
              (c) => (

                <li
                  key={c.id}
                  className="modern-selector-item"
                  onClick={() => {

                    setSubSelection(
                      c.name
                    );

                    setSearchQuery(
                      c.name
                    );

                    setCustomerSuggestions(
                      []
                    );

                  }}
                >

                  {c.name}

                </li>

              )
            )}

          </ul>

        )}

      </div>
    );
  }

  /* =====================================================
      PRODUCT
  ===================================================== */
  if (reportType === "product") {

    return (
      <div className="modern-inline-field-wrap">

        <div className="modern-inline-field">

          <label>
            Enter Product Name
          </label>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {

              const value =
                e.target.value;

              setSearchQuery(value);

              setSubSelection("");

            }}
            placeholder="Search product..."
            className="modern-inline-input"
          />

        </div>

        {productSuggestions.length > 0 && (

          <ul className="modern-selector-dropdown">

            {productSuggestions.map(
              (p) => (

                <li
                  key={p.id}
                  className="modern-selector-item"
                  onClick={() => {

                    setSearchQuery(
                      p.name
                    );

                    setSubSelection(
                      p.name
                    );

                    setProductSuggestions(
                      []
                    );

                  }}
                >

                  {p.name}

                </li>

              )
            )}

          </ul>

        )}

      </div>
    );
  }

  /* =====================================================
      DAY
  ===================================================== */
  if (reportType === "day") {

    return (
      <div className="modern-inline-field">

        <label>
          Select Day
        </label>

        <input
          type="date"
          value={subSelection}
          onChange={(e) =>
            setSubSelection(
              e.target.value
            )
          }
          className="modern-inline-input"
        />

      </div>
    );
  }

  /* =====================================================
      PAYMENT
  ===================================================== */
  if (reportType === "payment") {

    return (
      <div className="modern-inline-field">

        <label>
          Payment Method
        </label>

        <select
          value={subSelection}
          onChange={(e) =>
            setSubSelection(
              e.target.value
            )
          }
          className="modern-inline-input"
        >

          <option value="">
            -- Select --
          </option>

          <option value="Cash">
            Cash
          </option>

          <option value="Credit">
            Credit
          </option>

        </select>

      </div>
    );
  }

  return null;
};

 return (
  <div className="modern-reports-page">

    {/* =====================================================
        HERO
    ===================================================== */}
    <div className="modern-reports-hero">

      <div className="modern-reports-hero-left">

        <div className="modern-reports-pill">
          Reporting Intelligence
        </div>

        <h1>
          Reports
        </h1>

        <p>
          Generate operational,
          inventory, payment and
          transactional insights
          across the RCS ecosystem.
        </p>

      </div>

      {/* =====================================================
          ACTIONS
      ===================================================== */}
      <div className="modern-reports-actions">

        {/* GENERATE */}
        <button
          className="modern-report-action modern-report-action-teal"
          onClick={fetchReport}
        >

          <div className="modern-report-action-icon">
            <BarChart3 size={18} />
          </div>

          <div className="modern-report-action-content">

            <span>
              Generate Report
            </span>

            <p>
              Build report dataset
            </p>

          </div>

        </button>

        {/* PDF */}
        {reportData.length > 0 && (

          <button
            className="modern-report-action modern-report-action-red"
            onClick={exportToPDF}
          >

            <div className="modern-report-action-icon">
              <FileText size={18} />
            </div>

            <div className="modern-report-action-content">

              <span>
                Export PDF
              </span>

              <p>
                Download PDF report
              </p>

            </div>

          </button>

        )}

        {/* CSV */}
        {reportData.length > 0 && (

          <button
            className="modern-report-action modern-report-action-blue"
            onClick={exportToCSV}
          >

            <div className="modern-report-action-icon">
              <Sheet size={18} />
            </div>

            <div className="modern-report-action-content">

              <span>
                Export CSV
              </span>

              <p>
                Spreadsheet export
              </p>

            </div>

          </button>

        )}

      </div>

    </div>

    {/* =====================================================
        FILTERS
    ===================================================== */}
    <div className="modern-reports-filter-card">

      <div className="modern-reports-filter-grid">

        {/* TYPE */}
        <div className="modern-filter-field">

          <label>
            Report Type
          </label>

          <select
            value={reportType}
            onChange={(e) => {

              setReportType(
                e.target.value
              );

              setSearchQuery("");
              setSubSelection("");
              setStartDate("");
              setEndDate("");
              setReportData([]);

            }}
          >

            <option value="customer">
              By Customer
            </option>

            <option value="product">
              By Product
            </option>

            <option value="day">
              By Business Day
            </option>

            <option value="range">
              By Date Range
            </option>

            <option value="payment">
              By Payment Method
            </option>

            <option value="all-products">
              All Products
            </option>

          </select>

        </div>

        {/* START */}
        {[
          "customer",
          "product",
          "range",
          "payment",
          "all-products",
        ].includes(reportType) && (

          <>
            <div className="modern-filter-field">

              <label>
                Start Date
              </label>

              <input
                type="date"
                value={startDate}
                onChange={(e) =>
                  setStartDate(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="modern-filter-field">

              <label>
                End Date
              </label>

              <input
                type="date"
                value={endDate}
                onChange={(e) =>
                  setEndDate(
                    e.target.value
                  )
                }
              />

            </div>
          </>
        )}

{/* SEARCH */}
<div className="modern-filter-field modern-search-field">

  {renderSearchInput()}

</div>

      </div>

    </div>

    {/* =====================================================
        TABLE CARD
    ===================================================== */}
    <div className="modern-reports-table-card">

      <div className="modern-reports-card-header">

        <div>

          <h3>
            Report Results
          </h3>

          <span>
            Transactional reporting
            and operational analysis
          </span>

        </div>

      </div>

      <div className="modern-reports-table-wrap">

        {reportData.length > 0 ? (

          <table className="modern-reports-table">

            <thead>

              <tr>

                {Object.keys(
                  reportData[0]
                ).map((key, idx) => (

                  <th key={idx}>
                    {key.toUpperCase()}
                  </th>

                ))}

              </tr>

            </thead>

            <tbody>

              {reportData.map(
                (row, i) => (

                  <tr key={i}>

                    {Object.keys(
                      row
                    ).map(
                      (
                        key,
                        j
                      ) => {

                        /* ITEMS */
                        if (
                          key ===
                          "items"
                        ) {

                          let display =
                            "";

                          try {

                            let parsed =
                              [];

                            if (
                              Array.isArray(
                                row.items
                              )
                            ) {

                              parsed =
                                row.items;

                            } else if (
                              typeof row.items ===
                              "string"
                            ) {

                              parsed =
                                JSON.parse(
                                  row.items
                                );

                            }

                            display =
                              Array.isArray(
                                parsed
                              )
                                ? parsed
                                    .map(
                                      (
                                        item
                                      ) =>
                                        `${item.product || "Unnamed"} x${item.quantity}`
                                    )
                                    .join(
                                      ", "
                                    )
                                : row.items;

                          } catch {

                            display =
                              typeof row.items ===
                              "string"
                                ? row.items
                                : "–";

                          }

                          return (
                            <td key={j}>
                              {
                                display
                              }
                            </td>
                          );
                        }

                        /* PRODUCT REPORT SUBTOTAL */
                        if (
                          reportType ===
                            "product" &&
                          key ===
                            "subtotal"
                        ) {

                          const subtotal =
                            (
                              parseFloat(
                                row.quantity
                              ) || 0
                            ) *
                            (
                              parseFloat(
                                row.price
                              ) || 0
                            );

                          return (
                            <td key={j}>

                              {subtotal.toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                }
                              )}

                            </td>
                          );
                        }

                        /* PRODUCT REPORT TOTAL */
                        if (
                          reportType ===
                            "product" &&
                          key ===
                            "total"
                        ) {

                          const subtotal =
                            (
                              parseFloat(
                                row.quantity
                              ) || 0
                            ) *
                            (
                              parseFloat(
                                row.price
                              ) || 0
                            );

                          const discount =
                            parseFloat(
                              row.discount
                            ) || 0;

                          const total =
                            subtotal -
                            discount;

                          return (
                            <td key={j}>

                              {total.toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                }
                              )}

                            </td>
                          );
                        }

                        /* DEFAULT */
                        return (

                          <td key={j}>

                            {typeof row[
                              key
                            ] ===
                            "number"
                              ? row[
                                  key
                                ].toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                  }
                                )
                              : row[
                                  key
                                ]}

                          </td>

                        );
                      }
                    )}

                  </tr>

                )
              )}

            </tbody>

            {/* =====================================================
                FOOTER TOTALS
            ===================================================== */}
            <tfoot>

              <tr>

                {Object.keys(
                  reportData[0]
                ).map(
                  (
                    key,
                    i
                  ) => {

                    const isNumber =
                      typeof reportData[0][
                        key
                      ] ===
                      "number";

                    if (
                      i === 0
                    ) {

                      return (
                        <td key={i}>

                          <strong>
                            TOTAL
                          </strong>

                        </td>
                      );
                    }

                    if (
                      isNumber
                    ) {

                      const total =
                        reportData.reduce(
                          (
                            sum,
                            row
                          ) =>
                            sum +
                            (row[
                              key
                            ] || 0),
                          0
                        );

                      return (
                        <td key={i}>

                          <strong className="modern-report-total">

                            {total.toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                              }
                            )}

                          </strong>

                        </td>
                      );
                    }

                    return (
                      <td key={i}></td>
                    );
                  }
                )}

              </tr>

            </tfoot>

          </table>

        ) : (

          <div className="modern-reports-empty">

            <ClipboardList size={44} />

            <h4>
              No Report Data
            </h4>

            <p>
              Select filters and
              generate a report to
              view results.
            </p>

          </div>

        )}

      </div>

    </div>

  </div>
);
};

export default Reports;